// src/pages/Equipment/BulkActionModal.jsx
import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Modal, Form, Select, Table, Input, Space,
  Tag, Typography, Tooltip, message, Alert
} from 'antd';
import { equipmentAPI } from '../../api/client';

const { Text } = Typography;

/**
 * BulkActionModal
 *
 * Для большинства действий — стандартный модал (одно место / один сотрудник на всех).
 * Для «Выдано сотруднику» — таблица с колонкой места хранения:
 *   • Выбираешь общее место → оно проставляется в каждую строку
 *   • Любую строку можно переопределить отдельно
 *
 * props:
 *   open        — bool
 *   action      — объект из ACTIONS { key, label, needEmployee, needLocation, danger }
 *   selectedIds — string[]
 *   selectedItems — объекты оборудования (из текущей страницы таблицы)
 *   employees   — список сотрудников
 *   locations   — список мест хранения
 *   onClose     — () => void
 *   onSuccess   — (result, variables, actionKey) => void
 */
export default function BulkActionModal({
  open,
  action,
  selectedIds,
  selectedItems,
  employees,
  locations,
  onClose,
  onSuccess,
}) {
  const [form] = Form.useForm();

  // Для «Выдано сотруднику»: храним locationId для каждой позиции
  // key = equipment id, value = location_id (number | null)
  const [perRowLocation, setPerRowLocation] = useState({});
  // Общее место — проставляется как дефолт во все строки
  const [defaultLocation, setDefaultLocation] = useState(null);

  const isIssueAction = action?.key === 'Выдано сотруднику';

  // При открытии — сбрасываем per-row состояние
  useEffect(() => {
    if (open) {
      setPerRowLocation({});
      setDefaultLocation(null);
      form.resetFields();
    }
  }, [open, action]);

  // Когда меняется общее место — проставляем его во все строки
  // (только если строка ещё не переопределена)
  const handleDefaultLocationChange = (locId) => {
    setDefaultLocation(locId);
    // Перезаписываем все строки, которые не были вручную изменены
    // (здесь перезаписываем все — пользователь может потом переопределить)
    const updated = {};
    selectedItems.forEach(item => {
      updated[item.id] = locId;
    });
    setPerRowLocation(updated);
  };

  const handleRowLocation = (itemId, locId) => {
    setPerRowLocation(prev => ({ ...prev, [itemId]: locId }));
  };

  const locationOptions = locations.map(l => ({ label: l.name, value: l.id }));
  const employeeOptions = employees.map(e => ({
    label: `${e.full_name}${e.job_title ? ` — ${e.job_title}` : ''}`,
    value: e.id,
  }));

  const bulkMutation = useMutation({
    mutationFn: (data) => equipmentAPI.bulkAction(data),
    onSuccess: (res, variables) => {
      message.success(res.data.message);
      onSuccess(res.data, variables, action?.key);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  // Для «Выдано сотруднику» делаем отдельный вызов на каждую позицию
  // с индивидуальным location_id, либо батч если все места одинаковые
  const perItemMutation = useMutation({
    mutationFn: async ({ employee_id, notes, created_by }) => {
      const results = [];
      for (const item of selectedItems) {
        const loc = perRowLocation[item.id] ?? defaultLocation ?? null;
        const res = await equipmentAPI.performAction(item.id, {
          action:      'Выдано сотруднику',
          employee_id,
          location_id: loc,
          notes:       notes || null,
          created_by,
        });
        results.push(res.data);
      }
      return results;
    },
    onSuccess: (results, variables) => {
      message.success(`Выдано ${selectedItems.length} позиций`);
      // Передаём в onSuccess как будто это bulk — с equipment_ids и employee_id
      onSuccess(
        { message: `Выдано ${selectedItems.length} позиций` },
        { equipment_ids: selectedIds, employee_id: variables.employee_id, action: 'Выдано сотруднику' },
        'Выдано сотруднику',
      );
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка при выдаче'),
  });

  const handleSubmit = () => {
    form.validateFields().then(values => {
      const created_by = localStorage.getItem('login') || 'web';

      if (isIssueAction) {
        // Проверяем что для каждой строки задано место
        const missing = selectedItems.filter(item => !perRowLocation[item.id] && !defaultLocation);
        if (missing.length > 0) {
          message.warning(`Укажите место хранения для ${missing.length} позиций (или выберите общее место)`);
          return;
        }
        perItemMutation.mutate({
          employee_id: values.employee_id,
          notes:       values.notes || null,
          created_by,
        });
      } else {
        bulkMutation.mutate({
          equipment_ids: selectedIds,
          action:        action.key,
          employee_id:   values.employee_id || null,
          location_id:   values.location_id || null,
          notes:         values.notes       || null,
          created_by,
        });
      }
    });
  };

  if (!action) return null;

  const isPending = bulkMutation.isPending || perItemMutation.isPending;

  // Колонки таблицы для режима per-row location (выдача)
  const issueColumns = [
    {
      title: 'Инв. №', dataIndex: 'inventory_number', width: 110,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Тип', dataIndex: 'type', width: 110, ellipsis: true,
    },
    {
      title: 'Наименование', dataIndex: 'name', ellipsis: true,
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: 'Место хранения',
      key: 'location',
      width: 200,
      render: (_, record) => {
        const val = perRowLocation[record.id] ?? defaultLocation ?? undefined;
        const isOverridden = perRowLocation[record.id] !== undefined &&
                             perRowLocation[record.id] !== defaultLocation;
        return (
          <Select
            size="small"
            style={{ width: '100%' }}
            placeholder="Место…"
            value={val ?? undefined}
            onChange={(v) => handleRowLocation(record.id, v)}
            options={locationOptions}
            showSearch
            filterOption={(input, opt) =>
              (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            suffixIcon={
              isOverridden
                ? <Tooltip title="Переопределено"><span style={{ color: '#1677ff', fontSize: 10 }}>✎</span></Tooltip>
                : undefined
            }
          />
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      title={`${action.label} — ${selectedIds.length} позиций`}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={isPending}
      okText="Выполнить"
      cancelText="Отмена"
      okButtonProps={{ danger: action.danger }}
      width={isIssueAction ? 760 : 520}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>

        {/* Сотрудник (только для выдачи) */}
        {action.needEmployee && (
          <Form.Item
            name="employee_id"
            label="Сотрудник"
            rules={[{ required: true, message: 'Выберите сотрудника' }]}
          >
            <Select
              showSearch
              placeholder="Выберите сотрудника"
              filterOption={(input, opt) =>
                (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={employeeOptions}
            />
          </Form.Item>
        )}

        {/* Для выдачи — общее место + таблица с per-row override */}
        {isIssueAction && (
          <>
            <Form.Item
              label={
                <Space size={4}>
                  <span>Место хранения по умолчанию</span>
                  <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>применится ко всем</Tag>
                </Space>
              }
            >
              <Select
                showSearch
                placeholder="Выберите общее место (можно переопределить для каждой позиции)"
                allowClear
                value={defaultLocation ?? undefined}
                onChange={handleDefaultLocationChange}
                options={locationOptions}
                filterOption={(input, opt) =>
                  (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item label={`Позиции (${selectedItems.length})`} style={{ marginBottom: 8 }}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
                message="Можно переопределить место для отдельных позиций — выберите в строке таблицы"
              />
              <Table
                dataSource={selectedItems}
                columns={issueColumns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ y: 260 }}
              />
            </Form.Item>
          </>
        )}

        {/* Для остальных действий — одно место */}
        {!isIssueAction && action.needLocation && (
          <Form.Item
            name="location_id"
            label="Место хранения"
            rules={[{ required: true, message: 'Выберите место' }]}
          >
            <Select
              showSearch
              placeholder="Выберите место хранения"
              options={locationOptions}
              filterOption={(input, opt) =>
                (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}

        <Form.Item name="notes" label="Комментарий">
          <Input.TextArea rows={2} placeholder="Необязательно" />
        </Form.Item>
      </Form>
    </Modal>
  );
}